import { useEffect, useState } from "react";
import { Calculator } from "lucide-react";
import api, { formatApiError } from "../api";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const CALCS = [
  { id: "stamp-duty", label: "Stamp Duty" },
  { id: "registration-fee", label: "Registration Fee" },
  { id: "property-value", label: "Property Value" },
  { id: "jantri", label: "Jantri Value" },
  { id: "rent-agreement", label: "Rent Agreement" },
];

export default function Calculators() {
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({ property_value: "", area_sqm: "", district: "" });
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);

  const compute = async (type) => {
    try {
      const { data } = await api.post("/calculators/compute", {
        type,
        property_value: Number(form.property_value) || 0,
        area_sqm: Number(form.area_sqm) || 0,
        district: form.district,
      });
      setResult({ type, ...data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="calculators-page">
      <Seo title="Property Calculators Gujarat" description="Stamp duty, registration fee, jantri value, property value and rent agreement calculators for Gujarat." />
      <h1 className="font-display font-bold text-3xl md:text-4xl flex items-center gap-3">
        <Calculator className="text-primary" size={32} /> Calculators
      </h1>
      <p className="text-muted-foreground mt-2">Instant estimates. Rates are indicative and configurable; confirm current government rates at your SRO.</p>

      <Tabs defaultValue="stamp-duty" className="mt-6" onValueChange={() => setResult(null)}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="calculator-tabs">
          {CALCS.map((c) => <TabsTrigger key={c.id} value={c.id} data-testid={`calc-tab-${c.id}`}>{c.label}</TabsTrigger>)}
        </TabsList>
        {CALCS.map((c) => (
          <TabsContent key={c.id} value={c.id}>
            <div className="bg-white border border-border rounded-xl p-6" data-testid={`calc-panel-${c.id}`}>
              <h2 className="font-display font-semibold text-xl mb-4">{c.label} Calculator</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {["stamp-duty", "registration-fee", "property-value"].includes(c.id) && (
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Property Value (₹)</label>
                    <input type="number" className={input} value={form.property_value}
                      onChange={(e) => setForm({ ...form, property_value: e.target.value })} data-testid={`calc-value-${c.id}`} />
                  </div>
                )}
                {["jantri", "property-value"].includes(c.id) && (
                  <>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">District</label>
                      <select className={input} value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid={`calc-district-${c.id}`}>
                        <option value="">Other / Select</option>
                        {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">Area (sq. meter)</label>
                      <input type="number" className={input} value={form.area_sqm}
                        onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} data-testid={`calc-area-${c.id}`} />
                    </div>
                  </>
                )}
                {c.id === "rent-agreement" && (
                  <p className="sm:col-span-2 text-sm text-muted-foreground">
                    Flat-fee estimate for an 11-month leave & licence style agreement (stamp duty + registration + service fee).
                  </p>
                )}
              </div>
              <Button className="mt-5 bg-primary hover:bg-primary/90" onClick={() => compute(c.id)} data-testid={`calc-compute-${c.id}`}>Calculate</Button>

              {result && result.type === c.id && (
                <div className="mt-5 bg-muted rounded-lg p-5 space-y-1.5" data-testid={`calc-result-${c.id}`}>
                  {Object.entries(result).filter(([k]) => k !== "type").map(([k, v]) => {
                    const CURRENCY = new Set(["stamp_duty", "registration_fee", "jantri_value", "estimated_value", "service_fee", "total", "property_value", "jantri_rate_per_sqm"]);
                    const isPct = k.includes("percent");
                    const text = typeof v !== "number" ? String(v)
                      : isPct ? `${v}%`
                      : CURRENCY.has(k) ? `₹${v.toLocaleString("en-IN")}`
                      : v.toLocaleString("en-IN");
                    return (
                      <div key={k} className="flex justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="font-semibold">{text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
